import { useDebounce } from '@neo/hooks/use-debounce'
import type { ApiResponse } from '@shared/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios, { type AxiosError } from 'axios'
import { useMemo, useState } from 'react'
import { settingsService } from 'v2/shared/services/settings-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import { ConvChannels } from 'v2/types/api/conversations'
import type { RAgentNumberConflict, RAgentNumberPayload } from 'v2/types/api/settings'
import { errorToast, successToast } from 'v2/utils/toasts'
import {
  type AgentNumberRow,
  agentNumbersParams,
  isFullPage,
  numberUsage,
  readGlobalError,
  readNumberConflict,
  toNumberSearchPattern,
} from './agent-phone-numbers.utils'

export const AGENT_NUMBER_COPY = {
  attached: 'Number attached',
  removed: 'Number removed',
  attachFailed: 'Number not attached',
  removeFailed: 'Number not removed',
  inUseElsewhere: "This number is used by another agent and can't be moved here.",
  split: 'This number splits its calls between agents. Change it in the Plivo console.',
  hasApp: 'This number is connected to another app. Change it in the Plivo console.',
  changed: 'This number was changed elsewhere. The list is now up to date.',
  notFound: 'This number or agent is no longer available.',
} as const

const CONFLICT_COPY: Record<RAgentNumberConflict['code'], string> = {
  number_in_use: AGENT_NUMBER_COPY.inUseElsewhere,
  number_changed: AGENT_NUMBER_COPY.changed,
  number_has_split: AGENT_NUMBER_COPY.split,
  number_has_app: AGENT_NUMBER_COPY.hasApp,
}

const callNumbersParams = (pattern: string) =>
  pattern ? { channel: ConvChannels.CALL, pattern } : { channel: ConvChannels.CALL }

// Every key sits under accountNumbersBase, so one invalidate after a change refreshes them all.
const useNumbersQuery = (params: Record<string, unknown>, enabled = true) =>
  useQuery({
    queryKey: getSettingsQueryKeys().accountNumbers(params),
    queryFn: async (): Promise<AgentNumberRow[]> =>
      (await settingsService.getAccountNumbers(params)).data ?? [],
    enabled,
  })

const failureText = (error: unknown) => {
  const conflict = readNumberConflict(error)
  if (conflict) return CONFLICT_COPY[conflict.code]
  const globalError = readGlobalError(error)
  if (globalError) return globalError
  if (axios.isAxiosError(error) && error.response?.status === 404) return AGENT_NUMBER_COPY.notFound
  return getErrorText(error as AxiosError<ApiResponse<unknown>>)
}

// A split number this agent is in belongs in its list too, though it cannot be changed here.
const isAgentsNumber = (row: AgentNumberRow, agentId: string) => {
  const { kind } = numberUsage(row, agentId)
  return kind === 'attached' || kind === 'split'
}

export interface PendingMove {
  row: AgentNumberRow
  agentName: string
}

// Attach and detach apply at once through the embed number route; they are not part of the
// agent save, so nothing here reads the canvas edit mode.
export const useAgentPhoneNumbers = (agentId: string) => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const pattern = toNumberSearchPattern(useDebounce(search, 300))
  // The picker reads the plain list (capped at 100) and its search; the attached list reads the
  // agent lookup, so a number past the cap still shows as attached.
  const numbersQuery = useNumbersQuery(callNumbersParams(''))
  const searchQuery = useNumbersQuery(callNumbersParams(pattern), pattern !== '')
  const attachedQuery = useNumbersQuery(agentNumbersParams(agentId))
  // Kept after the dialog closes, so its text does not blank out during the close animation.
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null)
  const [isMoveOpen, setMoveOpen] = useState(false)

  const { mutate, isPending: isSaving } = useMutation({
    mutationFn: ({ digits, payload }: { digits: string; payload: RAgentNumberPayload }) =>
      settingsService.attachNumberToAgent(digits, payload),
    // Always refetch, never read the response row: a 200 can carry `data: null` (applied, but
    // the re-read failed), and a conflict means the list behind the choice was stale.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().accountNumbersBase }),
  })

  const attached = useMemo(
    () => (attachedQuery.data ?? []).filter((row) => isAgentsNumber(row, agentId)),
    [attachedQuery.data, agentId],
  )
  const isAttachedIncomplete = isFullPage(attachedQuery.data ?? [])
  const candidates = useMemo(
    () =>
      ((pattern ? searchQuery.data : numbersQuery.data) ?? []).filter(
        (row) => !isAgentsNumber(row, agentId),
      ),
    [pattern, searchQuery.data, numbersQuery.data, agentId],
  )

  const attach = (row: AgentNumberRow, replace = false) =>
    mutate(
      { digits: row.id, payload: { call_workflow_id: agentId, replace } },
      {
        onSuccess: () => successToast({ title: AGENT_NUMBER_COPY.attached }),
        onError: (error) => {
          const conflict = readNumberConflict(error)
          if (conflict?.code === 'number_in_use' && conflict.current_agent) {
            setPendingMove({ row, agentName: conflict.current_agent.name })
            setMoveOpen(true)
            return
          }
          errorToast({ title: AGENT_NUMBER_COPY.attachFailed, description: failureText(error) })
        },
      },
    )

  const remove = (row: AgentNumberRow) =>
    mutate(
      { digits: row.id, payload: { call_workflow_id: 'none', from_workflow_id: agentId } },
      {
        onSuccess: () => successToast({ title: AGENT_NUMBER_COPY.removed }),
        onError: (error) =>
          errorToast({ title: AGENT_NUMBER_COPY.removeFailed, description: failureText(error) }),
      },
    )

  return {
    numbersQuery,
    attachedQuery,
    attached,
    isAttachedIncomplete,
    candidates,
    isSearching: pattern !== '' && searchQuery.isFetching,
    setSearch,
    isSaving,
    attach,
    remove,
    pendingMove,
    isMoveOpen,
    setMoveOpen,
    confirmMove: () => {
      if (pendingMove) attach(pendingMove.row, true)
    },
  }
}
